import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { toast } from "sonner";
import { Gauge, DollarSign, MessageSquareText, ShieldAlert, Save, RefreshCw } from "lucide-react";
import { OverageTable } from "@/components/OverageTable";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmt = (n) => Number(n || 0).toLocaleString();

function pctColor(pct) {
  if (pct == null) return "text-muted-foreground";
  if (pct >= 100) return "text-redline-600";
  if (pct >= 80) return "text-amber-600";
  return "text-emerald-600";
}
function barColor(pct) {
  if (pct == null) return "bg-sand-300";
  if (pct >= 100) return "bg-redline-500";
  if (pct >= 80) return "bg-amber-500";
  return "bg-emerald-500";
}
function StatusPill({ pct, cap }) {
  if (!cap) return <span data-testid="status-none" className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-sand-100 text-muted-foreground">No cap</span>;
  if (pct >= 100) return <span data-testid="status-over" className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-redline-50 text-redline-700">Over cap</span>;
  if (pct >= 80) return <span data-testid="status-warn" className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Near cap</span>;
  return <span data-testid="status-ok" className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">On track</span>;
}

export default function AIUsage() {
  const [data, setData] = useCachedState("aiusage:cur", null);
  const [month, setMonth] = useState("");
  const [gcap, setGcap] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [threshold, setThreshold] = useState(80);
  const [smsRate, setSmsRate] = useState("");
  const [voiceRate, setVoiceRate] = useState("");
  const [caps, setCaps] = useState({});
  const [oaiCost, setOaiCost] = useState(null);

  const load = useCallback(async (m) => {
    const q = m ? `?month=${m}` : "";
    const r = await api.get(`/admin/usage${q}`);
    setData(r.data);
    setMonth(r.data.month);
    setGcap(r.data.global_cap ? String(r.data.global_cap) : "");
    setAlertEmail(r.data.alert_email || "");
    setThreshold(Math.round((r.data.alert_threshold || 0.8) * 100));
    setSmsRate(r.data.sms_rate_usd != null ? String(r.data.sms_rate_usd) : "");
    setVoiceRate(r.data.voice_rate_usd != null ? String(r.data.voice_rate_usd) : "");
    setCaps(Object.fromEntries(r.data.communities.map((c) => [c.tenant_id, c.cap ? String(c.cap) : ""])));
    try { const oc = await api.get(`/admin/openai-costs${m ? `?month=${m}` : `?month=${r.data.month}`}`); setOaiCost(oc.data); } catch { setOaiCost(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGlobal = async () => {
    try {
      await api.put("/admin/usage/settings", {
        global_cap_usd: parseFloat(gcap) || 0,
        alert_email: alertEmail,
        alert_threshold: (parseFloat(threshold) || 80) / 100,
        sms_rate_usd: parseFloat(smsRate) || 0,
        voice_rate_usd: parseFloat(voiceRate) || 0,
      });
      toast.success("Settings saved");
      load(month);
    } catch (e) { toast.error("Could not save"); }
  };

  const saveCap = async (tid) => {
    try {
      await api.put(`/tenants/${tid}/ai-cap`, { cap_usd: parseFloat(caps[tid]) || 0 });
      toast.success("Community cap saved");
      load(month);
    } catch (e) { toast.error("Could not save cap"); }
  };

  if (!data) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="ai-usage-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><Gauge className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> AI Usage & Cost</>} about={<>Real spend per community — AI model tokens + Twilio SMS segments — so you never go upside down. (Excludes operational texts like staff alerts &amp; OTP.)</>} />
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="usage-month-select" value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
            className="rounded-full bg-white border border-sand-200 px-4 py-2 text-sm font-medium outline-none focus:border-ink">
            {data.months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button data-testid="usage-refresh" onClick={() => load(month)} className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center hover:border-ink transition-colors">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* ACTUAL OpenAI spend — source of truth, pulled live from OpenAI billing */}
      {oaiCost?.configured && !oaiCost?.error && (
        <div data-testid="openai-actual-cost" className="bg-ink text-white rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-[0.14em] font-bold">
                <DollarSign className="h-4 w-4" /> Actual OpenAI spend · {oaiCost.month}
              </div>
              <div data-testid="openai-actual-total" className="font-heading text-4xl font-extrabold mt-2" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{money(oaiCost.total)}</div>
              <div className="text-xs text-white/50 mt-1">Pulled live from OpenAI billing — the source of truth (all OpenAI API usage on this org: voice + text + embeddings).</div>
            </div>
            {oaiCost.by_line_item?.length > 0 && (
              <div className="min-w-[200px]">
                <div className="text-[11px] uppercase tracking-wide text-white/40 font-bold mb-1.5">By line item</div>
                <div className="space-y-1">
                  {oaiCost.by_line_item.slice(0, 5).map((li) => (
                    <div key={li.item} className="flex items-center justify-between gap-6 text-sm">
                      <span className="text-white/70 truncate">{li.item}</span>
                      <span className="font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{money(li.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {oaiCost.daily?.some((d) => d.amount > 0) && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-[11px] uppercase tracking-wide text-white/40 font-bold mb-2">Daily</div>
              <div className="flex items-end gap-1 h-16">
                {(() => { const mx = Math.max(...oaiCost.daily.map((d) => d.amount), 0.0001); return oaiCost.daily.map((d) => (
                  <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${money(d.amount)}`}>
                    <div className="w-full bg-redline-500/80 rounded-t transition-all hover:bg-redline-400" style={{ height: `${Math.max(2, (d.amount / mx) * 64)}px` }} />
                  </div>
                )); })()}
              </div>
            </div>
          )}
          {oaiCost.by_community?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-[11px] uppercase tracking-wide text-white/40 font-bold mb-2">By community (exact, per OpenAI project)</div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
                {oaiCost.by_community.map((c) => (
                  <div key={c.name} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-white/70 truncate">{c.name}</span>
                    <span className="font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{money(c.amount)}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-white/40 mt-2">Communities show exact spend once provisioned (Configuration → OpenAI billing project). Usage before provisioning stays under "Shared / unassigned."</div>
            </div>
          )}
        </div>
      )}
      {oaiCost?.configured && oaiCost?.error && (
        <div data-testid="openai-actual-error" className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-800">{oaiCost.error}</div>
      )}

      {/* Global summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><DollarSign className="h-4 w-4" /> Total spend</div>
          <div data-testid="usage-total-cost" className="font-heading text-3xl font-extrabold mt-2">{money(data.total_cost)}</div>
          <div className="text-xs text-muted-foreground mt-1">AI {money(data.total_ai_cost)} · SMS {money(data.total_sms_cost)} · Voice {money(data.total_voice_cost)} · {month}</div>
        </div>
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><MessageSquareText className="h-4 w-4" /> AI messages</div>
          <div data-testid="usage-total-messages" className="font-heading text-3xl font-extrabold mt-2">{fmt(data.total_messages)}</div>
          <div className="text-xs text-muted-foreground mt-1">{data.avg_per_message != null ? `${money(data.avg_per_message)} avg / message` : "AI-generated replies"} · {fmt(data.total_texts)} SMS segs · {fmt(data.total_minutes)} voice min</div>
        </div>
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><ShieldAlert className="h-4 w-4" /> Global cap</div>
          <div className="font-heading text-3xl font-extrabold mt-2">
            {data.global_cap ? money(data.global_cap) : <span className="text-muted-foreground text-xl">Not set</span>}
          </div>
          {data.global_cap ? (
            <>
              <div className="h-2 rounded-full bg-sand-100 mt-3 overflow-hidden">
                <div className={`h-full ${barColor(data.global_pct)}`} style={{ width: `${Math.min(100, data.global_pct || 0)}%` }} />
              </div>
              <div className={`text-xs font-semibold mt-1 ${pctColor(data.global_pct)}`}>{data.global_pct}% used</div>
            </>
          ) : <div className="text-xs text-muted-foreground mt-1">Set a master monthly ceiling below</div>}
        </div>
      </div>

      {/* Global settings */}
      <div className="bg-white rounded-2xl border border-sand-200 p-5">
        <h2 className="font-heading font-bold text-lg mb-4">Master budget, rates &amp; alerts</h2>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
          <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">Global monthly cap ($)</span>
            <input data-testid="global-cap-input" type="number" min="0" step="1" value={gcap} onChange={(e) => setGcap(e.target.value)} placeholder="e.g. 500"
              className="mt-1 w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">SMS rate ($/segment)</span>
            <input data-testid="sms-rate-input" type="number" min="0" step="0.0001" value={smsRate} onChange={(e) => setSmsRate(e.target.value)} placeholder="0.0083"
              className="mt-1 w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">Voice rate ($/min)</span>
            <input data-testid="voice-rate-input" type="number" min="0" step="0.01" value={voiceRate} onChange={(e) => setVoiceRate(e.target.value)} placeholder="0.30"
              className="mt-1 w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">Alert email</span>
            <input data-testid="alert-email-input" type="email" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} placeholder="you@company.com"
              className="mt-1 w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">Warn at (% of cap)</span>
            <input data-testid="threshold-input" type="number" min="10" max="100" step="5" value={threshold} onChange={(e) => setThreshold(e.target.value)}
              className="mt-1 w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink" />
          </label>
        </div>
        <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground max-w-xl">Soft caps: the AI keeps answering when a cap is hit, but the community turns red here and (if an alert email is set) you'll get an email at the warn threshold and again when it goes over.</p>
          <button data-testid="save-global-settings" onClick={saveGlobal}
            className="flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </div>

      {/* Spend by model */}
      {(data.by_model || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden" data-testid="usage-by-model">
          <div className="px-5 py-4 border-b border-sand-200 flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">Spend by AI model · {month}</h2>
            <span className="text-xs text-muted-foreground">Concierge runs on GPT-5 · pricing varies by model</span>
          </div>
          <div className="divide-y divide-sand-100">
            {data.by_model.map((m) => (
              <div key={m.model} data-testid={`model-row-${m.model}`} className="px-5 py-3 flex items-center gap-4 flex-wrap">
                <span className="font-heading font-bold text-ink min-w-[150px]">{m.model}{m.kind === "voice" && <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold align-middle">Voice</span>}</span>
                <span className="text-sm text-muted-foreground">{m.kind === "voice" ? `${fmt(m.minutes)} min` : `${fmt(m.messages)} msgs`}</span>
                <span className="text-sm text-muted-foreground">{m.kind === "voice" ? `${fmt(m.calls)} calls` : `${fmt((m.input_tokens || 0) + (m.output_tokens || 0))} tokens`}</span>
                <span className="ml-auto font-heading font-extrabold text-ink">{money(m.cost)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-community table */}
      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-sand-200"><h2 className="font-heading font-bold text-lg">By community · {month}</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-sand-200">
                <th className="px-5 py-3 font-semibold">Community</th>
                <th className="px-3 py-3 font-semibold text-right">AI msgs</th>
                <th className="px-3 py-3 font-semibold text-right">SMS segs</th>
                <th className="px-3 py-3 font-semibold text-right">Voice min</th>
                <th className="px-3 py-3 font-semibold text-right">AI $</th>
                <th className="px-3 py-3 font-semibold text-right">SMS $</th>
                <th className="px-3 py-3 font-semibold text-right">Voice $</th>
                <th className="px-3 py-3 font-semibold text-right">Total</th>
                <th className="px-3 py-3 font-semibold text-right">$/msg</th>
                <th className="px-3 py-3 font-semibold">Monthly cap ($)</th>
                <th className="px-3 py-3 font-semibold">Used</th>
                <th className="px-5 py-3 font-semibold text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.communities.map((c) => (
                <tr key={c.tenant_id} data-testid={`usage-row-${c.tenant_id}`} className="border-b border-sand-100 last:border-0 hover:bg-sand-50">
                  <td className="px-5 py-3 font-semibold text-ink">{c.name}</td>
                  <td className="px-3 py-3 text-right">{fmt(c.messages)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{fmt(c.texts)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{fmt(c.minutes)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{money(c.ai_cost)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{money(c.sms_cost)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{money(c.voice_cost)}</td>
                  <td className="px-3 py-3 text-right font-semibold">{money(c.cost)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground">{c.avg_per_message != null ? money(c.avg_per_message) : "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      <input data-testid={`cap-input-${c.tenant_id}`} type="number" min="0" step="1" value={caps[c.tenant_id] ?? ""} placeholder="none"
                        onChange={(e) => setCaps({ ...caps, [c.tenant_id]: e.target.value })}
                        className="w-24 rounded-lg bg-sand-50 border border-sand-200 px-2 py-1.5 text-sm outline-none focus:border-ink" />
                      <button data-testid={`cap-save-${c.tenant_id}`} onClick={() => saveCap(c.tenant_id)}
                        className="h-8 w-8 rounded-lg bg-ink text-white flex items-center justify-center hover:bg-ink-700 transition-colors" title="Save cap">
                        <Save className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3 min-w-[120px]">
                    {c.cap ? (
                      <>
                        <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden w-24">
                          <div className={`h-full ${barColor(c.pct)}`} style={{ width: `${Math.min(100, c.pct || 0)}%` }} />
                        </div>
                        <span className={`text-xs font-semibold ${pctColor(c.pct)}`}>{c.pct}%</span>
                      </>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right"><StatusPill pct={c.pct} cap={c.cap} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <OverageTable month={month} />
    </div>
  );
}
